import type { ScenarioDelta } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';
import {
  scenarioDeltaToPositionIntents,
  type PositionIntentCore,
  type ResolvedPosition,
  type UnsupportedDeltaOp,
} from './scenarioDeltaToPositionIntents';
import {
  scenarioDeltaToFieldIntents,
  withoutScenarioFieldUpdates,
} from './scenarioDeltaToFieldIntents';

/**
 * Commit bridge, slice 2 (jobs-first plan, Phase 3): orchestrate committing a
 * client scenario's delta into the canonical org model. Pure and injectable so
 * it can be unit-tested without React/fetch — the hook supplies the real
 * `stageIntent`/`applyChangeSet`/`newIntentId`.
 *
 * Each translated intent is staged into ONE server scenario change-set, with the
 * head + graph version threaded forward from each response (the append after the
 * first must reuse the fresh head, or it 409s). The caller then either applies,
 * prepares, or leaves that complete named head for dashboard review.
 */

export interface StageOutcome {
  readonly changeSetId: string;
  readonly scenarioId?: string | null;
  readonly graphVersion: number;
}

export interface CommitScenarioInput {
  readonly delta: ScenarioDelta;
  readonly recordIdByNodeId: (nodeId: string) => string | undefined;
  readonly positionByRecordId: (recordId: string) => ResolvedPosition | undefined;
  /** Folder record id → whether it is a canonical Person record. */
  readonly isPersonRecord?: (recordId: string) => boolean;
  readonly ownerDiagramId: string;
  readonly baseGraphVersion: number;
  readonly effectiveAt: string;
  readonly scenarioName: string;
  readonly newIntentId: () => string;
  readonly stageIntent: (intent: PositionStructureIntent) => Promise<StageOutcome>;
  /** `stage` leaves the proposed scenario for dashboard review. */
  readonly completion?: 'apply' | 'prepare_operational' | 'stage';
  /** Required only for the direct `apply` completion path. */
  readonly applyChangeSet?: (changeSetId: string) => Promise<void>;
  /** Converts the complete named scenario head into one operational draft; null leaves it staged for review. */
  readonly prepareOperationalChangeSet?: (changeSetId: string) => Promise<string | null>;
}

export interface CommitScenarioResult {
  readonly status: 'applied' | 'prepared' | 'staged' | 'nothing_to_commit';
  readonly appliedCount: number;
  readonly unsupported: readonly UnsupportedDeltaOp[];
  readonly changeSetId?: string;
  readonly scenarioId?: string;
  /** Server-owned operational draft opened by the review dialog. */
  readonly operationalChangeSetId?: string;
}

export function toIntent(
  core: PositionIntentCore,
  transport: {
    intentId: string;
    expectedGraphVersion: number;
    ownerDiagramId: string;
    effectiveAt: string;
    scenarioId?: string;
    head?: string;
    scenarioName?: string;
  },
): PositionStructureIntent {
  const common = {
    intentId: transport.intentId,
    expectedGraphVersion: transport.expectedGraphVersion,
    mode: 'scenario' as const,
    ownerDiagramId: transport.ownerDiagramId,
    effectiveAt: transport.effectiveAt,
    scenarioId: transport.scenarioId,
    scenarioChangeSetId: transport.head,
    expectedHeadChangeSetId: transport.head,
    scenarioName: transport.scenarioName,
  };
  if (core.kind === 'edit') {
    return { ...common, kind: 'edit', positionId: core.positionId, title: core.title, department: core.department, level: core.level };
  }
  if (core.kind === 'create') {
    return {
      ...common,
      kind: 'create',
      title: core.title,
      department: core.department,
      level: core.level,
      managerPositionId: core.managerPositionId,
      hire: core.hire,
    };
  }
  if (core.kind === 'retire') {
    return {
      ...common,
      kind: 'retire',
      positionId: core.positionId,
      reportingPolicy: core.reportingPolicy,
    };
  }
  if (core.kind === 'retire_person') {
    return { ...common, kind: 'retire_person', personId: core.personRecordId };
  }
  if (core.kind === 'update_person') {
    return { ...common, kind: 'update_person', personId: core.personRecordId, fields: core.fields };
  }
  if (core.kind === 'add_secondary_reporting') {
    return {
      ...common,
      kind: 'add_secondary_reporting',
      positionId: core.positionId,
      managerPositionId: core.managerPositionId,
      relationshipType: core.relationshipType,
    };
  }
  return { ...common, kind: 'reparent', positionId: core.positionId, managerPositionId: core.managerPositionId };
}

export async function commitScenarioDelta(input: CommitScenarioInput): Promise<CommitScenarioResult> {
  const fieldTranslation = scenarioDeltaToFieldIntents({
    delta: input.delta,
    recordIdByNodeId: input.recordIdByNodeId,
    isPersonRecord: input.isPersonRecord,
  });
  const translation = scenarioDeltaToPositionIntents({
    delta: withoutScenarioFieldUpdates(input.delta),
    recordIdByNodeId: input.recordIdByNodeId,
    positionByRecordId: input.positionByRecordId,
    isPersonRecord: input.isPersonRecord,
  });
  const unsupported = [...translation.unsupported, ...fieldTranslation.unsupported];
  const intents = [...translation.intents, ...fieldTranslation.intents];

  if (intents.length === 0) {
    return { status: 'nothing_to_commit', appliedCount: 0, unsupported };
  }

  let scenarioId: string | undefined;
  let head: string | undefined;
  let operationalChangeSetId: string | undefined;
  let version = input.baseGraphVersion;

  for (const core of intents) {
    const intent = toIntent(core, {
      intentId: input.newIntentId(),
      expectedGraphVersion: version,
      ownerDiagramId: input.ownerDiagramId,
      effectiveAt: input.effectiveAt,
      scenarioId,
      head,
      // Name the scenario only on the first (creating) intent.
      scenarioName: scenarioId ? undefined : input.scenarioName,
    });
    const outcome = await input.stageIntent(intent);
    scenarioId = outcome.scenarioId ?? scenarioId;
    head = outcome.changeSetId;
    version = outcome.graphVersion ?? version;
  }

  if (intents.length > 0 && !head) {
    // Should not happen (intents.length > 0), but never apply a missing head.
    return { status: 'nothing_to_commit', appliedCount: 0, unsupported };
  }

  const completion = input.completion ?? 'apply';
  if (head && completion === 'apply') {
    if (!input.applyChangeSet) throw new Error('Change-set apply callback is required');
    await input.applyChangeSet(head);
  }
  if (head && completion === 'prepare_operational') {
    if (!input.prepareOperationalChangeSet) {
      throw new Error('Operational preparation callback is required');
    }
    operationalChangeSetId = await input.prepareOperationalChangeSet(head) ?? undefined;
  }

  return {
    status: completion === 'apply' ? 'applied' : operationalChangeSetId ? 'prepared' : 'staged',
    appliedCount: intents.length,
    unsupported,
    changeSetId: head,
    scenarioId,
    operationalChangeSetId,
  };
}
